import { useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';
import { AnimatedNode } from '@ui/components/Animation';
import { Card } from '@ui/components/Card';
import { IconButton } from '@ui/components/IconButton';
import { IconSettings } from '@ui/icons-buttons/IconSettings';

import { MediaDevices } from '@features/video/hooks/useMediaDevices';
import { MediaDeviceSettings } from '@features/video/ui/MediaDeviceSettings';
import { StreamControls } from '@features/video/ui/StreamControls';

import styles from './RefereeControls.module.css';

export type Props = {
  className?: string;
  micOn: boolean;
  disabled?: boolean;
  devices: MediaDevices;
  // трек для индикатора уровня в эфире; без него уровень с захвата
  meterTrack?: MediaStreamTrack | null;
  onToggleMic: () => void;
  // без него кнопки выхода нет: в модалке привата выход — крестик
  onLeave?: () => void;
};

// Контролы судьи: настройки устройств звука, микрофон и выход
export function RefereeControls({
  className,
  micOn,
  disabled,
  devices,
  meterTrack,
  onToggleMic,
  onLeave,
}: Props) {
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  // Клик снаружи закрывает всплывашку, но шестеренка — внутри области: ее повторное
  // нажатие закрывает, а не закрывает и тут же открывает снова
  const settingsRef = useClickOutside<HTMLDivElement>(isSettingsOpen ? closeSettings : undefined);
  useOnEscape(isSettingsOpen ? closeSettings : undefined);

  return (
    <div className={cx(styles.controls, className)}>
      <div className={styles.controls__settings} ref={settingsRef}>
        <IconButton
          className={cx(styles.controls__gear, { [styles.is_active]: isSettingsOpen })}
          preset="gray"
          size="32"
          title="Настройки звука"
          onClick={() => setSettingsOpen((v) => !v)}
        >
          <IconSettings />
        </IconButton>
        <AnimatedNode className={styles.controls__popover} animation="opacity">
          {isSettingsOpen && (
            <Card className={styles.controls__card}>
              <MediaDeviceSettings audio devices={devices} meterTrack={meterTrack} />
            </Card>
          )}
        </AnimatedNode>
      </div>
      <StreamControls
        size="s"
        micOn={micOn}
        disabled={disabled}
        onToggleMic={onToggleMic}
        onHangUp={onLeave}
      />
    </div>
  );
}
